/*
 * VivaNutrition — dormant fallback for Shopify's legacy cart helpers.
 *
 * STATUS — CORRECTED 2026-08-17. READ THIS BEFORE CHANGING ANYTHING.
 * This file was originally written as the fix for a P0 cart bug, on the belief that
 * `{{ 'api.jquery.js' | shopify_asset_url }}` had been retired and returned 404.
 * That belief was WRONG. The library is alive:
 *
 *     https://<shop>/cdn/shopifycloud/storefront/assets/themes_support/
 *         api.jquery-7ab1a3a4.js   ->   HTTP 200, 9128 bytes, 24 Shopify.* symbols
 *
 * The 404 came from probing a guessed path (`cdn.shopify.com/s/javascripts/...`)
 * instead of the fingerprinted URL Liquid actually renders. Shopify.getCart and
 * Shopify.formatMoney were never missing, so they were never the cause of the stale
 * cart total. The real cause was the two Liquid/JS defects fixed alongside this file:
 * the invalid hyphenated `addcart-susscess` identifier and the `=` used in place of
 * `==`. Those fixes stand; this file is not what repaired the bug.
 *
 * WHY IT IS STILL HERE
 * head-script.liquid loads it BEFORE the platform library, and the platform library
 * assigns unconditionally, so the real implementation overwrites every symbol below.
 * At runtime this file is therefore inert. It costs one render-blocking request and
 * buys a genuine safety net if Shopify ever does retire these helpers. It is a
 * fallback, not a fix — do not cite it as one, and do not reorder the loads.
 *
 * Verify before trusting either reading:
 *     python scripts/shopify/verify_platform_assets.py --symbols
 *
 * See docs/activation/shopify-legacy-cdn-library-status-2026-08-17.md
 *
 * DESIGN RULES
 * 1. Non-destructive. Every definition is guarded by a typeof check, and the platform
 *    library loads afterwards, so the original always wins.
 * 2. No forced page reload. The cart is re-read from /cart.js, which is the supported
 *    endpoint, with caching explicitly defeated.
 * 3. No new dependency. Plain ES5 + fetch with an XHR fallback; no jQuery requirement
 *    even though the theme happens to load jQuery.
 */
window.Shopify = window.Shopify || {};

(function (Shopify) {
  'use strict';

  /* ---------------------------------------------------------------- money */

  /*
   * Shopify.formatMoney — faithful port of the platform helper.
   * Supports every documented money_format placeholder. Falls back to the
   * two-decimal dot form when the shop format is missing or unrecognised.
   */
  if (typeof Shopify.formatMoney !== 'function') {
    Shopify.formatMoney = function (cents, format) {
      if (typeof cents === 'string') cents = cents.replace('.', '');

      var value = '';
      var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      var formatString = format || Shopify.money_format || window.money_format || '${{amount}}';

      function defaultTo(number, size, character) {
        if (number === null || isNaN(number)) return character || '0';
        var padded = number.toString();
        while (padded.length < size) padded = '0' + padded;
        return padded;
      }

      function formatWithDelimiters(number, precision, thousands, decimal) {
        precision = typeof precision === 'number' ? precision : 2;
        thousands = thousands === undefined ? ',' : thousands;
        decimal = decimal === undefined ? '.' : decimal;

        if (isNaN(number) || number === null) return '0';

        number = (number / 100.0).toFixed(precision);
        var parts = number.split('.');
        var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        var fraction = parts[1] ? decimal + parts[1] : '';

        return whole + fraction;
      }

      var match = formatString.match(placeholderRegex);
      switch (match ? match[1] : 'amount') {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_with_space_separator':
          value = formatWithDelimiters(cents, 2, ' ', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = formatWithDelimiters(cents, 0, ' ');
          break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 2, "'", '.');
          break;
        default:
          value = formatWithDelimiters(cents, 2);
          break;
      }

      /* defaultTo is referenced by the padded-format variants used by some locales */
      void defaultTo;

      return formatString.replace(placeholderRegex, value);
    };
  }

  /* ----------------------------------------------------------- transport */

  function request(url, options, onSuccess, onError) {
    options = options || {};

    /*
     * Cache is defeated deliberately. A cached GET /cart.js is the classic cause of a
     * mini-cart that shows the pre-add total, which would reproduce the very bug this
     * file fixes.
     */
    if (typeof window.fetch === 'function') {
      var init = {
        method: options.method || 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Accept': 'application/json' }
      };
      if (options.body) {
        init.body = options.body;
        init.headers['Content-Type'] = 'application/x-www-form-urlencoded';
      }
      window
        .fetch(url, init)
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.json();
        })
        .then(function (data) {
          if (typeof onSuccess === 'function') onSuccess(data);
        })
        .catch(function (err) {
          if (typeof onError === 'function') onError(err);
        });
      return;
    }

    var xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url, true);
    xhr.setRequestHeader('Accept', 'application/json');
    if (options.body) {
      xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
    }
    xhr.onload = function () {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          if (typeof onSuccess === 'function') onSuccess(JSON.parse(xhr.responseText));
        } catch (e) {
          if (typeof onError === 'function') onError(e);
        }
      } else if (typeof onError === 'function') {
        onError(new Error('HTTP ' + xhr.status));
      }
    };
    xhr.onerror = function () {
      if (typeof onError === 'function') onError(new Error('network error'));
    };
    xhr.send(options.body || null);
  }

  function bustCache(url) {
    return url + (url.indexOf('?') === -1 ? '?' : '&') + '_vn=' + Date.now();
  }

  /* ----------------------------------------------------------------- cart */

  if (typeof Shopify.getCart !== 'function') {
    Shopify.getCart = function (callback) {
      request(
        bustCache('/cart.js'),
        {},
        function (cart) {
          if (typeof callback === 'function') callback(cart);
        },
        function (err) {
          /* Fail loudly in the console rather than silently leaving a stale total. */
          if (window.console && console.warn) {
            console.warn('[VN] Shopify.getCart failed:', err);
          }
        }
      );
    };
  }

  if (typeof Shopify.changeItem !== 'function') {
    Shopify.changeItem = function (line, quantity, callback) {
      request(
        '/cart/change.js',
        { method: 'POST', body: 'line=' + encodeURIComponent(line) + '&quantity=' + encodeURIComponent(quantity) },
        function (cart) {
          if (typeof callback === 'function') callback(cart);
        }
      );
    };
  }

  /*
   * Shopify.removeItem historically accepted a variant id. /cart/change.js accepts an
   * id as well as a line, so the variant-id call sites in ss_custom.js keep working.
   */
  if (typeof Shopify.removeItem !== 'function') {
    Shopify.removeItem = function (id, callback) {
      request(
        '/cart/change.js',
        { method: 'POST', body: 'id=' + encodeURIComponent(id) + '&quantity=0' },
        function (cart) {
          if (typeof callback === 'function') callback(cart);
        }
      );
    };
  }

  if (typeof Shopify.updateCartNote !== 'function') {
    Shopify.updateCartNote = function (note, callback) {
      request(
        '/cart/update.js',
        { method: 'POST', body: 'note=' + encodeURIComponent(note) },
        function (cart) {
          if (typeof callback === 'function') callback(cart);
        }
      );
    };
  }

  /* ------------------------------------------------------------ utilities */

  if (typeof Shopify.resizeImage !== 'function') {
    Shopify.resizeImage = function (src, size) {
      try {
        if (!src || size === 'original') return src;
        var match = src.match(/(.*\/[\w\-_.]+)\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif|webp)(\?.*)?$/i);
        if (!match) return src;
        return match[1] + '_' + size + '.' + match[2] + (match[3] || '');
      } catch (e) {
        return src;
      }
    };
  }

  if (typeof Shopify.uniq !== 'function') {
    Shopify.uniq = function (array) {
      var seen = [];
      for (var i = 0; i < array.length; i++) {
        if (seen.indexOf(array[i]) === -1) seen.push(array[i]);
      }
      return seen;
    };
  }
})(window.Shopify);
